import styled, { css, keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   COLLECTION MODAL — 60/40 SPLIT (Image | Actions Panel)
   Light water theme. Clean vertical structure in the right panel:
     header → meta → action groups → flex spacer → CTA pills.
   X sits inside the right panel (top-right, absolute).
   ═══════════════════════════════════════════════════════════════════════ */

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

// ─── Shell ─────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  --media-aspect: ${({ $aspectRatio }) => $aspectRatio || "1 / 1"};
  --media-aspect-num: ${({ $aspectNum }) => $aspectNum || 1};
  --footer-reserve: 280px;
  position: relative;
  display: flex;
  flex-direction: row;
  /* Width auto-sizes from the image aspect + fixed-width info panel.
     Height is fixed — image fits fully within it (height-governed). */
  width: auto;
  max-width: 75dvw;
  height: min(80dvh, 580px);
  min-height: ${({ $minHeight }) => $minHeight || "420px"};
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  /* Shell blur lowered (was 28px). bg above is 0.94-0.96 alpha so blur
     barely shows. panelFloat brightness keyframe also dropped. */
  backdrop-filter: blur(12px) saturate(180%);
  -webkit-backdrop-filter: blur(12px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);

  [data-theme="dark"] & {
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }
  /* overflow stays visible so the side ArrowButtonWrappers (left:-84px,
     right:-84px) render outside the modal. Inner media + scroll panes
     have their own overflow:hidden + border-radius clipping. */
  overflow: visible;

  @media (max-width: 768px) {
    flex-direction: column;
    width: min(95dvw, calc(82svh * var(--media-aspect-num)));
    height: auto;
    max-width: 95dvw;
    max-height: 82svh;
    min-height: auto;
    aspect-ratio: var(--media-aspect);
    border-radius: 24px;
    background: #000;
  }

  @media (max-width: 330px) {
    min-width: 100%;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
  }
`;

// ─── LEFT PANEL — Media area (height-governed, width from aspect) ──────
//  The image is fully visible (object-fit: contain). Width is derived
//  from modal height × image aspect ratio — so a vertical image becomes
//  narrow, a horizontal image becomes wide (capped). The modal shrinks
//  or stretches accordingly.

export const MediaArea = styled.div`
  position: relative;
  flex: 0 1 auto;
  height: 100%;
  aspect-ratio: var(--media-aspect);
  /* Cap so an ultra-wide landscape image can't push the modal past the viewport. */
  max-width: calc(92dvw - 320px);
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  padding: 0;
  z-index: 1;
  overflow: hidden;
  background: rgba(var(--glass-pale-rgb), 0.55);
  /* Round the corners that touch the outer modal so the panel itself
     follows the modal's 28px radius (not just the inner image). */
  border-top-left-radius: 28px;
  border-bottom-left-radius: 28px;

  @media (max-width: 900px) {
    max-width: calc(92dvw - 300px);
  }

  @media (max-width: 768px) {
    position: absolute;
    inset: 0;
    flex: none;
    width: 100%;
    max-width: 100%;
    height: 100%;
    aspect-ratio: auto;
    max-height: none;
    padding: 0;
    background: #000;
    border-radius: 24px;
  }
`;

/* Media fills its aspect-matched column. object-fit: contain so the
   whole image shows without being cropped. Border-radius follows the
   outer modal only on the corners touching the modal edge. */
const mediaFrame = `
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-top-left-radius: 28px;
  border-bottom-left-radius: 28px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  @media (max-width: 768px) {
    border-radius: 24px;
  }
`;

export const ImageElement = styled.img`
  ${mediaFrame}
  transition: opacity 180ms ease;
`;

export const VideoElement = styled.video`
  ${mediaFrame}
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 180ms ease;
`;

// ─── RIGHT PANEL — Actions (40%) ───────────────────────────────────────
//  Export name kept as `Footer` for JS compat.

export const Footer = styled.div`
  position: relative;
  flex: 0 0 320px;
  width: 320px;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 32px 24px;
  border-left: 1px solid rgba(var(--color-brand-300-rgb), 0.22);
  background: linear-gradient(
    180deg,
    rgba(var(--glass-white-rgb), 0.72) 0%,
    rgba(var(--glass-pale-rgb), 0.88) 45%,
    rgba(var(--glass-pale-rgb), 0.95) 100%
  );
  box-shadow:
    inset 1px 0 0 rgba(255, 255, 255, 0.85),
    inset -3px 0 10px rgba(var(--color-brand-200-rgb), 0.12);
  overflow-y: auto;
  /* Round the corners that touch the outer modal so the right panel
     follows the modal's 28px radius. */
  border-top-right-radius: 28px;
  border-bottom-right-radius: 28px;

  scrollbar-width: thin;
  scrollbar-color: rgba(var(--color-brand-300-rgb), 0.4) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-brand-300-rgb), 0.4);
    border-radius: 999px;
  }

  @media (max-width: 900px) {
    flex: 0 0 300px;
    width: 300px;
    padding: 28px 22px;
  }

  @media (max-width: 768px) {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
    flex: none;
    width: 100%;
    padding: 12px 12px 14px;
    gap: 8px;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.5) 45%,
      rgba(0, 0, 0, 0.82) 100%
    );
    box-shadow: none;
    border: none;
    border-radius: 0 0 24px 24px;
    overflow: visible;
  }
  [data-theme="dark"] {
    border: none;
    box-shadow: none;
  }
`;

// ─── Close button — absolute top-right INSIDE right panel ──────────────

export const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.85) 0%,
    rgba(var(--glass-white-rgb), 0.58) 45%,
    rgba(var(--glass-pale-rgb), 0.42) 100%
  );
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.2);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy),
    background 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 15px;
    height: 15px;
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.05);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);

    svg path {
      stroke: var(--color-brand-300);
    }
  }
`;

// ─── Navigation arrows — external to the modal (unchanged API) ─────────

export const ArrowButtonWrapper = styled.div`
  position: absolute;
  z-index: 4;
  top: 50%;
  left: ${({ $right }) => ($right ? "unset" : "-84px")};
  right: ${({ $right }) => ($right ? "-84px" : "unset")};
  transform: translateY(-50%);

  @media (max-width: 1180px) {
    left: ${({ $right }) => ($right ? "unset" : "-60px")};
    right: ${({ $right }) => ($right ? "-60px" : "unset")};
  }

  @media (max-width: 768px) {
    top: unset;
    bottom: -48px;
    left: ${({ $right }) => ($right ? "unset" : "0")};
    right: ${({ $right }) => ($right ? "0" : "unset")};
    transform: none;
  }
`;

export const ArrowButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.65);
  border: none;
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.82) 0%,
    rgba(var(--glass-white-rgb), 0.54) 45%,
    rgba(var(--glass-pale-rgb), 0.4) 100%
  );
  box-shadow:
    0 8px 20px rgba(var(--color-brand-500-rgb), 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  color: var(--color-brand-700);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 6px;
    width: 18px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.5);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.5),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
    color: var(--color-brand-300);

    svg path {
      stroke: var(--color-brand-300);
    }
  }
`;

// ─── Header row (title + credit pill) ──────────────────────────────────

export const InfoBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 36px;
  margin-bottom: 32px;
  /* keep title clear of the absolute Close button (top-right) */
  padding-right: 48px;

  @media (max-width: 768px) {
    min-height: 0;
    margin-bottom: 6px;
    padding-right: 44px;
  }
`;

export const ModelName = styled.div`
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-primary);
  font-size: 26px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.022em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 2px 4px rgba(var(--color-brand-600-rgb), 0.14));

  @media (max-width: 900px) {
    font-size: 22px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.2;
    -webkit-line-clamp: 1;
    background: none;
    -webkit-background-clip: border-box;
    background-clip: border-box;
    -webkit-text-fill-color: #fff;
    color: #fff;
    filter: none;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
  }
`;

/* Credit / seed pill — kept frosted water */
export const SeedPill = styled.button`
  position: absolute;
  display: inline-flex;
  align-items: center;
  right: 16px;
  top: 16px;
  gap: 5px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 55%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  box-shadow:
    0 4px 10px rgba(var(--color-brand-500-rgb), 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.95);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  border-radius: var(--radius-pill);
  /* "Water outline" — white inner rim against aqua glow halo */
  border: 1.5px solid var(--glass-edge);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.32) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  color: var(--glass-text-on-pill);

  box-shadow:
    /* outer water halo ring — the "outline" effect reads as wetness */
    0 0 0 3px rgba(var(--color-brand-300-rgb), 0.22),
    0 0 0 1px rgba(var(--glass-white-rgb), 0.35),
    /* deep brand drop shadow */ 0 14px 28px
      rgba(var(--color-brand-600-rgb), 0.38),
    0 4px 10px rgba(var(--color-brand-600-rgb), 0.2),
    /* inner specular rim + underglow */ inset 0 1px 0
      rgba(var(--glass-white-rgb), 0.9),
    inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38),
    inset 0 0 0 1px rgba(255, 255, 255, 0.35);

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    transform: translateY(-1px);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow:
      0 12px 24px rgba(0, 0, 0, 0.55),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  @media (max-width: 768px) {
    right: auto;
    left: 16px;
  }
`;

// ─── Optional meta row ("Generated 2h ago") ────────────────────────────

export const Meta = styled.div`
  color: var(--color-brand-700);
  opacity: 0.6;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  margin-top: -6px;
`;

// ─── Section label (optional "Actions") ───────────────────────────────

export const SectionLabel = styled.div`
  color: var(--color-brand-700);
  opacity: 0.55;
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

// ─── Divider ───────────────────────────────────────────────────────────

export const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: linear-gradient(
    90deg,
    rgba(var(--color-brand-300-rgb), 0) 0%,
    rgba(var(--color-brand-300-rgb), 0.24) 50%,
    rgba(var(--color-brand-300-rgb), 0) 100%
  );
  margin: 0 0 16px;

  /* Toolbar collapses to a single row on mobile, so the horizontal
     divider is replaced by a per-group vertical separator (see
     ButtonGroup mobile rules). Hide the bar entirely here. */
  @media (max-width: 768px) {
    display: none;
  }
`;

// ─── Actions section — vertical stack of grouped rows ─────────────────
//  Export kept as `Toolbar` for JS compat. No gap — each ButtonGroup
//  controls its own bottom margin via the $mb prop.

export const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  /* Collapse all action groups into a single horizontal cluster on
     mobile so the modal stays compact instead of stacking groups
     vertically. The Divider is hidden and per-group margins zeroed
     via media queries on those components. */
  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

/* Horizontal group of icon buttons. $mb = margin-bottom in px.
   On mobile the parent Toolbar becomes a single flex row, so groups
   stop stacking and per-group bottom margins collapse to 0. */
export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? `${$mb}px` : "0")};

  @media (max-width: 768px) {
    margin-bottom: 0;
    gap: 6px;
    /* Add a thin separator line between groups so the visual grouping
       still reads ("ratings | file actions | delete") even though
       they share one row. */
    &:not(:last-child)::after {
      content: "";
      align-self: center;
      width: 1px;
      height: 20px;
      margin-left: 4px;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.4) 50%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }
`;

/* Each action icon button — 44x44, 12px squircle, blue-tinted white */
export const ToolbarButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid
    ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "rgba(255, 120, 143, 0.42)";
      if ($ratingUp) return "rgba(94, 226, 164, 0.52)";
      if ($ratingDown) return "rgba(255, 120, 143, 0.52)";
      return "rgba(var(--glass-white-rgb), 0.62)";
    }};
  background: ${({ $ratingUp, $ratingDown, $danger }) => {
    if ($danger)
      return `radial-gradient(
        120% 100% at 20% 0%,
        rgba(255, 240, 243, 0.78) 0%,
        rgba(255, 220, 228, 0.55) 55%,
        rgba(255, 205, 216, 0.42) 100%
      )`;
    if ($ratingUp)
      return `radial-gradient(
        120% 100% at 20% 0%,
        rgba(232, 255, 245, 0.82) 0%,
        rgba(204, 246, 226, 0.58) 55%,
        rgba(172, 232, 206, 0.48) 100%
      )`;
    if ($ratingDown)
      return `radial-gradient(
        120% 100% at 20% 0%,
        rgba(255, 240, 243, 0.82) 0%,
        rgba(255, 220, 228, 0.58) 55%,
        rgba(255, 205, 216, 0.48) 100%
      )`;
    return `radial-gradient(
      130% 100% at 30% 15%,
      rgba(var(--glass-white-rgb), 0.82) 0%,
      rgba(var(--glass-white-rgb), 0.54) 45%,
      rgba(var(--glass-pale-rgb), 0.42) 100%
    )`;
  }};
  box-shadow: ${({ $ratingUp, $ratingDown, $danger }) => {
    if ($danger)
      return `0 4px 10px rgba(211, 71, 99, 0.2),
              inset 0 1px 0 rgba(255, 255, 255, 0.85)`;
    if ($ratingUp)
      return `0 4px 10px rgba(16, 185, 129, 0.22),
              inset 0 1px 0 rgba(255, 255, 255, 0.85)`;
    if ($ratingDown)
      return `0 4px 10px rgba(211, 71, 99, 0.22),
              inset 0 1px 0 rgba(255, 255, 255, 0.85)`;
    return `0 4px 10px rgba(var(--color-brand-500-rgb), 0.18),
            inset 0 1px 0 rgba(255, 255, 255, 0.95),
            inset 0 -2px 6px rgba(var(--color-brand-200-rgb), 0.18)`;
  }};
  color: ${({ $ratingUp, $ratingDown, $danger }) => {
    if ($danger) return "#d34763";
    if ($ratingUp) return "#0d9060";
    if ($ratingDown) return "#d34763";
    return "var(--color-brand-700)";
  }};
  cursor: pointer;
  outline: none;
  /* overflow: hidden; */
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    box-shadow 180ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 8px;
    right: 8px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.75) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg,
  l-bouncy {
    position: relative;
    z-index: 1;
    width: 20px;
    height: 20px;
  }

  svg path {
    stroke: currentColor;
  }

  &:hover {
    transform: translateY(-1px) scale(1.05);
    border-color: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "rgba(255, 120, 143, 0.72)";
      if ($ratingUp) return "rgba(94, 226, 164, 0.72)";
      if ($ratingDown) return "rgba(255, 120, 143, 0.72)";
      return "rgba(var(--color-brand-400-rgb), 0.5)";
    }};
    filter: brightness(1.02);
  }

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "rgba(211, 71, 99, 0.32)";
      if ($ratingUp) return "rgba(40, 180, 96, 0.32)";
      if ($ratingDown) return "rgba(255, 89, 89, 0.32)";
      return "rgba(255, 255, 255, 0.14)";
    }};
    backdrop-filter: blur(14px) saturate(160%);
    -webkit-backdrop-filter: blur(14px) saturate(160%);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    color: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "#ffadbb";
      if ($ratingUp) return "#9af1be";
      if ($ratingDown) return "#ffadbb";
      return "#ffffff";
    }};
    svg,
    l-bouncy {
      width: 16px;
      height: 16px;
    }
    svg path {
      stroke: currentColor;
    }
    ${({ $danger }) =>
      $danger &&
      css`
        > div {
          bottom: 6px !important;
        }
      `}
    &::before {
      display: none;
    }
  }

  [data-theme="dark"] & {
    border-color: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "rgba(255, 120, 143, 0.32)";
      if ($ratingUp) return "rgba(94, 226, 164, 0.32)";
      if ($ratingDown) return "rgba(255, 120, 143, 0.32)";
      return "transparent";
    }};
    background: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "rgba(211, 71, 99, 0.16)";
      if ($ratingUp) return "rgba(16, 185, 129, 0.16)";
      if ($ratingDown) return "rgba(211, 71, 99, 0.16)";
      return "var(--color-surface-3)";
    }};
    box-shadow: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "0 4px 10px rgba(0, 0, 0, 0.4)";
      if ($ratingUp) return "0 4px 10px rgba(0, 0, 0, 0.4)";
      if ($ratingDown) return "0 4px 10px rgba(0, 0, 0, 0.4)";
      return "0 4px 10px rgba(0, 0, 0, 0.45), inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16)";
    }};
    color: ${({ $ratingUp, $ratingDown, $danger }) => {
      if ($danger) return "#ff7896";
      if ($ratingUp) return "#5ee2a4";
      if ($ratingDown) return "#ff7896";
      return "var(--color-brand-300)";
    }};
  }
`;

/* Legacy spacer — neutralized (no flex push); kept for JS compat */
export const ToolbarSpacer = styled.div`
  display: none;
`;

// ─── CTA section — flows DIRECTLY below the delete button ──────────────
//  Export name kept as `ActionRow` for JS compat. No margin-top: auto,
//  no flex push — content flows naturally from top.

export const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  /* On the overlay layout the two CTAs sit side-by-side so the modal
     footer stays compact and doesn't cover too much of the video.
     Compact dimensions + nowrap prevent the labels from wrapping to
     two lines inside the narrow side-by-side pills. */
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: stretch;
    gap: 6px;
    > * {
      flex: 1 1 0;
      min-width: 0;
      width: auto !important;
    }
    button {
      height: 38px !important;
      padding: 0 10px !important;
      font-size: 12px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

/* Primary action pill — 56px, 28px radius, brand-gradient */
export const ActionButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 56px;
  padding: 0 22px;
  border-radius: 28px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.38),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy),
    opacity 200ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 18px;
    right: 18px;
    height: 11px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Moving shimmer */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.32) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4.5s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.48),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    height: 52px;
    border-radius: 26px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
  }
`;

export const ActionButtonText = styled.span`
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.005em;
  white-space: nowrap;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;
